import { Observable } from 'rxjs';
import { Inject, Injectable } from '../../injection';

import { EmptyResponse } from '../data/empty-response.do';
import { RegisterViewModel } from '../data/register-view-model.do';
import { ResetPasswordViewModel } from '../data/reset-password-view-model.do';
import { SignUpViewModel } from '../data/sign-up-view-model.do';
import { User } from '../data/user.do';
import { VerificationId } from '../data/verification-id.do';
import { VerificationStatus } from '../data/verification-status.do';
import { ApiEndpointConfiguration } from '../interfaces/api-endpoint-configuration.interface';
import { IMobizHttpService } from '../interfaces/mobiz-http.interface';
import { MobizServerApi } from '../other/mobiz-server-api.enum';

import { IAccountService } from './account.interface';
import { IMobizStateService } from './mobiz-state.interface';

@Injectable()
export class AccountService implements IAccountService {
  constructor(
    @Inject(IMobizHttpService)
    private http: IMobizHttpService,
    @Inject(IMobizStateService)
    private state: IMobizStateService,
  ) {
  }

  /**
   * @inheritdoc
   */
  public signUp(signUp: SignUpViewModel): Observable<VerificationId> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountSignUp,
      body: JSON.stringify(signUp),
    };
    return this.http.post(new VerificationId(), config);
  }

  /**
   * @inheritdoc
   */
  public checkEmailVerificationStatus(verificationId: VerificationId): Observable<VerificationStatus> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountEmailVerificationStatus,
      urlParameters: {
        verificationId: verificationId.verificationId
      }
    };
    return this.http.get(new VerificationStatus(), config);
  }

  /**
   * @inheritdoc
   */
  public resendVerificationEmail(verificationId: VerificationId): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountResendVerificationEmail,
      urlParameters: {
        verificationId: verificationId.verificationId
      }
    };
    return this.http.post(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public register(registration: RegisterViewModel): Observable<RegisterViewModel> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountRegister,
      body: JSON.stringify(registration),
    };
    return this.http.post(new RegisterViewModel(), config);
  }

  /**
   * @inheritdoc
   */
  public resetPassword(payload: ResetPasswordViewModel): Observable<RegisterViewModel> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountResetPassword,
      body: JSON.stringify(payload),
    };
    return this.http.post(new RegisterViewModel(), config);
  }

  /**
   * @inheritdoc
   */
  public changeNotificationEmail(email: string): Observable<EmptyResponse> | never {
    if (!this.state.currentUser.value) {
      throw new Error('No user logged in.');
    }
    const user: User = this.state.currentUser.value;
    user.defaultEmail = email;

    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.User,
      body: JSON.stringify(user),
      urlParameters: {
        userId: user.id
      }
    };
    return this.http.put(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public changePassword(input: { oldPassword: string; password: string; confirmPassword: string }): Observable<EmptyResponse> {
    const data: Record<string, string> = {
      OldPassword: input.oldPassword,
      Password: input.password,
      ConfirmPassword: input.confirmPassword,
    };

    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountChangePassword,
      body: JSON.stringify(data),
    };

    return this.http.put(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public confirmEmail(payload: {userId: string; code: string}): Observable<EmptyResponse> {
    let config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountConfirmEmail,
      body: JSON.stringify(payload)
    };

    return this.http.post(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public requestEmailVerification(email: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountEmailRequestVerification,
      body: JSON.stringify({email: email}),

    };
    return this.http.post(new EmptyResponse(), config);
  }

  /**
   * @inheritdoc
   */
  public requestPhoneNumberVerificationToken(phoneNumber: string): Observable<VerificationId> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountPhoneNumberRequestVerification,
      body: JSON.stringify({phoneNumber: phoneNumber}),
    };
    return this.http.post(new VerificationId(), config);
  }

  /**
   * @inheritdoc
   */
  public checkPhoneNumberVerificationToken(verificationId: VerificationId, token: string): Observable<EmptyResponse> {
    const config: ApiEndpointConfiguration = {
      serverApi: MobizServerApi.AccountPhoneNumberCheckVerificationToken,
      urlParameters: {
        verificationId: verificationId.verificationId
      },
      body: JSON.stringify({token: token}),
    };
    return this.http.post(new EmptyResponse(), config);
  }
}
